// Public (pre-login) organization details shown in the auth-page footers.
//
// Why this exists: the organization name, representative and address used to be three literals in the
// `auth` message catalog. That made the front end a second source of truth for who operates the service,
// and it is why the footer and the rest of the product disagreed for months after the operating
// organization changed (SHCT-624). They now come from the backend, which reads the one `institution`
// database row, so an organization change is a database update with no rebuild and no redeploy.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';

/**
 * What the footer renders. Every field is optional: the organization row allows a missing address or
 * representative, and a client must render the absence rather than an empty labelled row.
 *
 * The address arrives WITHOUT a label prefix — the label ("주소: ") belongs to the screen, because the
 * admin footer shows one and the medical footer does not.
 */
export interface PublicInstitution {
  name: string | null;
  address: string | null;
  representative: string | null;
}

interface ApiEnvelope<T> {
  data: T;
}

/**
 * Fetch the organization details. Public — no Authorization header and no token, because the footer
 * renders on login / signup / password-reset, all of which are reached before a session exists.
 *
 * Throws on a network or server failure; the caller decides what an absent footer should look like.
 */
export async function fetchPublicInstitution(): Promise<PublicInstitution> {
  const res = await httpClient.get<ApiEnvelope<PublicInstitution>>(endpoints.institution.public);
  const data = res.data?.data;
  if (!data || typeof data !== 'object') {
    throw new Error('institution: unexpected response shape');
  }
  return {
    name: data.name ?? null,
    address: data.address ?? null,
    representative: data.representative ?? null,
  };
}
